import React, { useState, useEffect } from 'react';
import { Lock, Unlock, Copy, Check, Upload, Download, RefreshCw, FileText, AlertCircle } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';

interface HistoryItem {
  id: string;
  input: string;
  output: string;
  operation: 'encode' | 'decode';
  timestamp: string;
}

function Base64Encoder() {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [operation, setOperation] = useState<'encode' | 'decode'>('encode');
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [autoConvert, setAutoConvert] = useState(true);
  const [inputType, setInputType] = useState<'text' | 'file'>('text');
  const [fileName, setFileName] = useState<string>('');

  // Base64 encoding function
  const encodeToBase64 = (text: string): string => {
    try {
      return btoa(unescape(encodeURIComponent(text)));
    } catch (err) {
      throw new Error('Failed to encode text to Base64');
    }
  };

  // Base64 decoding function
  const decodeFromBase64 = (base64: string): string => {
    try {
      return decodeURIComponent(escape(atob(base64)));
    } catch (err) {
      throw new Error('Invalid Base64 string');
    }
  };

  // File to Base64 encoding
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Convert function
  const convert = (input: string, op: 'encode' | 'decode') => {
    setError(null);
    
    if (!input.trim()) {
      setOutputText('');
      return;
    }

    try {
      let result = '';
      if (op === 'encode') {
        result = encodeToBase64(input);
      } else {
        result = decodeFromBase64(input);
      }
      
      setOutputText(result);
      
      // Add to history
      const historyItem: HistoryItem = {
        id: Math.random().toString(36).substr(2, 9),
        input: input,
        output: result,
        operation: op,
        timestamp: new Date().toLocaleString()
      };
      
      setHistory(prev => [historyItem, ...prev].slice(0, 20)); // Keep last 20 items
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Conversion failed');
      setOutputText('');
    }
  };

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputText(value);
    if (autoConvert) {
      convert(value, operation);
    }
  };

  // Handle operation change
  const handleOperationChange = (newOperation: 'encode' | 'decode') => {
    setOperation(newOperation);
    if (inputText && autoConvert) {
      convert(inputText, newOperation);
    }
  };

  // Handle manual convert
  const handleManualConvert = () => {
    convert(inputText, operation);
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(type);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setInputType('file');
    
    try {
      const base64 = await fileToBase64(file);
      setInputText(`data:${file.type};base64,${base64}`);
      if (autoConvert) {
        setOutputText(base64);
        setOperation('encode');
      }
    } catch (err) {
      setError('Failed to process file');
    }
  };

  // Download as file
  const downloadAsFile = () => {
    if (!outputText) return;
    
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${operation === 'encode' ? 'encoded' : 'decoded'}_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Clear all
  const clearAll = () => {
    setInputText('');
    setOutputText('');
    setError(null);
    setFileName('');
    setInputType('text');
  };

  // Clear history
  const clearHistory = () => {
    setHistory([]);
  };

  // Load from history
  const loadFromHistory = (item: HistoryItem) => {
    setInputText(item.input);
    setOutputText(item.output);
    setOperation(item.operation);
    setInputType('text');
    setFileName('');
  };

  // Auto-convert on operation change
  useEffect(() => {
    if (inputText && autoConvert) {
      convert(inputText, operation);
    }
  }, [operation, autoConvert]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="Base64 Encoder" category="dev-utils" />
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Lock size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>Base64 Encoder/Decoder</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Encode and decode text or files to/from Base64 format</p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Operation Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Operation
            </label>
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => handleOperationChange('encode')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-l-md border ${
                  operation === 'encode'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <Lock className="h-4 w-4 inline mr-2" />
                Encode
              </button>
              <button
                onClick={() => handleOperationChange('decode')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                  operation === 'decode'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <Unlock className="h-4 w-4 inline mr-2" />
                Decode
              </button>
            </div>
          </div>

          {/* Input Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Input Type
            </label>
            <select
              value={inputType}
              onChange={(e) => setInputType(e.target.value as 'text' | 'file')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="text">Text Input</option>
              <option value="file">File Upload</option>
            </select>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoConvert}
                  onChange={(e) => setAutoConvert(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Auto convert</span>
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {!autoConvert && (
            <button
              onClick={handleManualConvert}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Convert
            </button>
          )}
          <button
            onClick={clearAll}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Input/Output Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Input */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {operation === 'encode' ? 'Plain Text' : 'Base64 Encoded'}
            </h2>
            {inputText && (
              <button
                onClick={() => copyToClipboard(inputText, 'input')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                title="Copy input"
              >
                {copiedText === 'input' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            )}
          </div>

          {inputType === 'text' ? (
            <textarea
              value={inputText}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={operation === 'encode' ? 'Enter text to encode...' : 'Enter Base64 string to decode...'}
              className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none font-mono text-sm"
            />
          ) : (
            <div className="h-64 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex items-center justify-center">
              <div className="text-center">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
                >
                  Choose File
                </label>
                {fileName && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {fileName}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Output */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {operation === 'encode' ? 'Base64 Encoded' : 'Plain Text'}
            </h2>
            <div className="flex gap-2">
              {outputText && (
                <>
                  <button
                    onClick={() => copyToClipboard(outputText, 'output')}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    title="Copy output"
                  >
                    {copiedText === 'output' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={downloadAsFile}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    title="Download as file"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {error ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          ) : (
            <textarea
              value={outputText}
              readOnly
              placeholder={`${operation === 'encode' ? 'Base64 encoded' : 'Decoded'} result will appear here...`}
              className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white resize-none font-mono text-sm bg-gray-50"
            />
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="h-5 w-5" />
                History ({history.length})
              </h2>
              <button
                onClick={clearHistory}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Clear History
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                  onClick={() => loadFromHistory(item)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.operation === 'encode' 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {item.operation === 'encode' ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                      {item.operation.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {item.timestamp}
                    </span>
                  </div>
                  <div className="text-sm">
                    <div className="text-gray-600 dark:text-gray-400 mb-1">
                      Input: <span className="font-mono">{item.input.length > 50 ? item.input.substring(0, 50) + '...' : item.input}</span>
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      Output: <span className="font-mono">{item.output.length > 50 ? item.output.substring(0, 50) + '...' : item.output}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Information */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">About Base64</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">What is Base64?</h4>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Base64 is a binary-to-text encoding scheme that represents binary data in an ASCII string format. 
              It's commonly used to encode binary data for transmission over text-based protocols.
            </p>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Common Use Cases:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Email attachments</li>
              <li>• Data URLs in web development</li>
              <li>• API authentication tokens</li>
              <li>• Storing binary data in JSON</li>
              <li>• Embedding images in CSS/HTML</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Features:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Real-time encoding/decoding</li>
              <li>• File upload support</li>
              <li>• History tracking</li>
              <li>• Copy to clipboard</li>
              <li>• Download results as files</li>
              <li>• UTF-8 text support</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Base64Encoder;
